/**
 * Two-Factor Authentication Verify Component (Story 1.7)
 * Used during login to verify 2FA code.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "../atoms/Input";
import { Button } from "../atoms/Button";
import { ShieldCheckIcon, KeyIcon } from "@heroicons/react/24/outline";

interface TwoFactorVerifyProps {
  onVerify: (code: string, useBackup: boolean) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  error?: string | null;
}

export function TwoFactorVerify({
  onVerify,
  onCancel,
  loading = false,
  error,
}: TwoFactorVerifyProps) {
  const { t } = useTranslation("auth");
  const [code, setCode] = useState("");
  const [useBackup, setUseBackup] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!useBackup && code.length !== 6) {
      setLocalError(t("twofa_code_required"));
      return;
    }

    if (useBackup && code.length < 6) {
      setLocalError(t("twofa_backup_required"));
      return;
    }

    setLocalError(null);
    await onVerify(code, useBackup);
  };

  const displayError = error || localError;

  return (
    <div className="w-full max-w-sm mx-auto">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
          <ShieldCheckIcon className="w-8 h-8 text-primary-600 dark:text-primary-400" />
        </div>
        <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
          {t("twofa_title")}
        </h2>
        <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
          {useBackup
            ? t("twofa_backup_prompt")
            : t("twofa_authenticator_prompt")}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label={useBackup ? t("twofa_backup_code_label") : t("twofa_auth_code_label")}
          type="text"
          inputMode={useBackup ? "text" : "numeric"}
          maxLength={useBackup ? 20 : 6}
          value={code}
          onChange={(e) => {
            const val = useBackup
              ? e.target.value.toUpperCase()
              : e.target.value.replace(/\D/g, "").slice(0, 6);
            setCode(val);
            setLocalError(null);
          }}
          error={displayError || undefined}
          placeholder={useBackup ? "XXXXXXXX" : "000000"}
          autoFocus
          autoComplete="one-time-code"
          className={useBackup ? "font-mono" : "text-center text-2xl tracking-widest font-mono"}
        />

        <Button
          type="submit"
          variant="primary"
          loading={loading}
          disabled={code.length < (useBackup ? 6 : 6)}
          className="w-full"
        >
          {t("twofa_verify")}
        </Button>

        <div className="flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => {
              setUseBackup(!useBackup);
              setCode("");
              setLocalError(null);
            }}
            className="text-sm text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
          >
            <KeyIcon className="w-4 h-4" />
            {useBackup ? t("twofa_use_authenticator") : t("twofa_use_backup")}
          </button>
        </div>

        <Button type="button" variant="ghost" onClick={onCancel} className="w-full">
          {t("twofa_cancel")}
        </Button>
      </form>

      {useBackup && (
        <p className="mt-4 text-xs text-center text-neutral-500 dark:text-neutral-400">
          {t("twofa_lost_access")}
        </p>
      )}
    </div>
  );
}
